import { postAndDownloadFile } from '@/utils';
import { ApiResponse } from '@/utils';
import { getCurrentLanguage } from '@/i18n/config';
import { enhancedRequest } from '@/utils/enhancedRequest';
import {
  AssetListParams,
  AssetListResponse,
  AssetDeleteParams,
  AssetOperationParams,
  AssetDownloadParams,
  AssetDownloadResponse,
  AssetGroupingParams,
  AssetUploadResponse,
  UploadModule,
  UploadSource,
} from '../types';

/**
 * AMAI资产管理服务
 * 用于管理图片、视频等资产的上传、下载、分组等操作
 */
class AssetService {
  /**
   * 获取资产列表（带防重复请求和缓存）
   * @param params 查询参数 { asset_type?, favorite?, group_id?, model_id?, page_number?, page_size?, time_desc?}
   * @returns 资产列表
   */
  async getAssetList(params: AssetListParams): Promise<ApiResponse<AssetListResponse>> {
    // 使用增强的POST请求，自动处理缓存、去重和错误重试
    console.log('[AssetService.getAssetList] params =', JSON.stringify(params));
    return enhancedRequest.post<AssetListResponse>('/asset/list', params, {
      useCache: true,
      cacheTTL: 5 * 60 * 1000, // 5分钟缓存
      enableDeduplication: true,
      requestKey: `asset-list:${JSON.stringify(params)}`,
      retryCount: 2, // 失败时重试2次
      retryDelay: 1000, // 重试间隔1秒
    });
  }

  /**
   * 删除资产（带防重复请求和自动清理相关缓存）
   * @param params 参数 { asset_id_list} 或 { group_id }
   * @returns 操作结果
   */
  async deleteAsset(params: AssetDeleteParams): Promise<ApiResponse<null>> {
    return enhancedRequest.post<null>('/asset/delete', params, {
      enableDeduplication: true,
      requestKey: `asset-delete:${JSON.stringify(params)}`,
      retryCount: 1,
    });
  }

  /**
   * 下载资产
   * @param params 参数 { asset_id_list?, group_id?}
   * @param fileName 可选的文件名，主要用于文件夹下载
   * @returns 下载链接
   */
  async downloadAsset(
    params: AssetDownloadParams,
    fileName?: string
  ): Promise<ApiResponse<AssetDownloadResponse>> {
    await postAndDownloadFile('/asset/download', params, fileName);

    return Promise.resolve({
      code: 0,
      data: { url: '' },
      message: 'Download started.',
      success: true,
    });
  }

  /**
   * 添加资产到收藏夹（带防重复请求和自动清理相关缓存）
   * @param params 参数 { asset_id_list}
   * @returns 操作结果
   */
  async markAssetFavorite(params: AssetOperationParams): Promise<ApiResponse<null>> {
    return enhancedRequest.post<null>('/asset/mark', params, {
      enableDeduplication: true,
      requestKey: `asset-mark:${JSON.stringify(params)}`,
      retryCount: 1,
    });
  }

  /**
   * 资产分组调整（带防重复请求和自动清理相关缓存）
   * @param params 参数 {list: [{asset_id, new_group_id?, old_group_id?}]}
   * @returns 操作结果
   */
  async adjustAssetGrouping(params: AssetGroupingParams): Promise<ApiResponse<null>> {
    return enhancedRequest.post<null>('/asset/grouping', params, {
      enableDeduplication: true,
      requestKey: `asset-grouping:${JSON.stringify(params)}`,
      retryCount: 1,
    });
  }

  /**
   * 上传文件（用于认证场景：企业认证、AIGC认证）
   * @param file 要上传的文件
   * @returns 上传结果
   */
  async uploadFileForAuth(file: File): Promise<ApiResponse<AssetUploadResponse>> {
    try {
      console.log('开始上传认证文件:', file.name, '类型:', file.type, '大小:', file.size);

      if (!file || file.size === 0) {
        throw new Error('无效的文件');
      }

      const formData = new FormData();
      formData.append('file', file, file.name);

      // const token = localStorage.getItem('anmo_ai_token');
      // const baseURL = (import.meta as any).env.VITE_APP_BASE_URL || '';
      // const apiPrefix = (import.meta as any).env.VITE_APP_BASE_API || '';
      // const url = `${baseURL}${apiPrefix}/auth/upload`;

      // prod走实际，其他走本地代理
      const isProd = import.meta.env.MODE === 'prod';
      console.log('isProd', isProd);
      console.log('process.env.NODE_ENV', process.env.NODE_ENV);
      const baseURL = isProd ? 
      `${import.meta.env.VITE_APP_BASE_URL || ''}${import.meta.env.VITE_APP_BASE_API || ''}` : 
      import.meta.env.VITE_APP_BASE_API || '';
      const url = `${baseURL}/auth/upload`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          // Authorization: token ? `Bearer ${token}` : '',
          'Accept-Language': getCurrentLanguage(),
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`文件上传失败: ${response.statusText || errorText}`);
      }

      const result = await response.json();

      if (result.code !== 0) {
        throw new Error(result.msg || result.message || '文件上传失败');
      }

      if (result.code === 0 && result.data) {
        if (!result.data.asset_id || !result.data.url) {
          console.error('上传成功但资产信息不完整:', result.data);
          return {
            code: 500,
            data: {} as AssetUploadResponse,
            message: '服务器返回的资产信息不完整',
            success: false,
          };
        }

        return {
          code: 0,
          data: {
            asset_id: result.data.asset_id,
            asset_type: 0,
            size: file.size,
            url: result.data.url,
          },
          message: result.msg || 'ok',
          success: true,
        };
      }

      return {
        code: result.code,
        data: {} as AssetUploadResponse,
        message: result.msg || result.message || '文件上传失败',
        success: false,
      };
    } catch (error) {
      console.error('上传认证文件出错:', error);
      throw error;
    }
  }

  /**
   * 检查文件是否已存在（通过文件名在用户资产中查找）
   * @param fileName 文件名
   * @param assetType 资产类型，默认视频(2)
   * @returns 已存在的资产信息，如果不存在返回null
   */
  async checkFileExists(fileName: string, assetType: number = 2): Promise<ApiResponse<{ asset_id: string; url: string; asset_type: number } | null>> {
    try {
      // 查询用户上传的视频资产（source=1表示用户上传）
      const response = await enhancedRequest.post<AssetListResponse>('/asset/list', {
        asset_type: assetType,
        source: UploadSource.UserUpload,
        page_number: 1,
        page_size: 1000, // 假设用户资产不会超过1000个，需要分页获取更多可以用递归
        time_desc: 0,
      }, {
        useCache: false, // 不使用缓存，获取最新数据
        enableDeduplication: true,
      });

      if (response.code === 0 && response.data && response.data.list) {
        // 查找同名文件
        const existingAsset = response.data.list.find(asset => {
          // 匹配文件名（考虑URL编码的情况）
          const urlFileName = decodeURIComponent(asset.url.split('/').pop() || '');
          return urlFileName === fileName || asset.name === fileName;
        });

        if (existingAsset) {
          return {
            code: 0,
            data: {
              asset_id: existingAsset.asset_id,
              url: existingAsset.url,
              asset_type: existingAsset.asset_type,
            },
            message: '文件已存在',
            success: true,
          };
        }
      }

      return {
        code: 0,
        data: null,
        message: '文件不存在',
        success: true,
      };
    } catch (error) {
      console.error('检查文件是否存在失败:', error);
      return {
        code: 0,
        data: null,
        message: '检查失败',
        success: true,
      };
    }
  }

  /**
   * 上传文件
   * @param file 要上传的文件
   * @param module 模块类型，默认为1
   * @param source 上传来源，默认为1（用户上传）
   * @returns 上传结果
   */
  async uploadFile(
    file: File,
    module: number | UploadModule = UploadModule.Default,
    source: number | UploadSource = UploadSource.UserUpload,
    creationSource?: number,
    name?: string
  ): Promise<ApiResponse<AssetUploadResponse>> {
    try {
      console.log(
        '开始上传文件:',
        file.name,
        '类型:',
        file.type,
        '大小:',
        file.size,
        '模块:',
        module,
        '来源:',
        source,
        '创建来源:',
        creationSource
      );

      if (!file || file.size === 0) {
        throw new Error('无效的文件');
      }

      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('module', module.toString());
      formData.append('source', source.toString());
      if (creationSource !== undefined) {
        formData.append('creation_source', creationSource.toString());
      }
      if (name !== undefined) {
        formData.append('name', name);
      }

      const token = localStorage.getItem('anmo_ai_token');
      // const baseURL = (import.meta as any).env.VITE_APP_BASE_URL || '';
      // const apiPrefix = (import.meta as any).env.VITE_APP_BASE_API || '';
      // const url = `${baseURL}${apiPrefix}/asset/upload`;

      // prod走实际，其他走本地代理
      const isProd = import.meta.env.MODE === 'prod';
      console.log('isProd', isProd);
      console.log('process.env.NODE_ENV', process.env.NODE_ENV);
      const baseURL = isProd ? 
      `${import.meta.env.VITE_APP_BASE_URL || ''}${import.meta.env.VITE_APP_BASE_API || ''}` : 
      import.meta.env.VITE_APP_BASE_API || '';
      const url = `${baseURL}/asset/upload`;


      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Accept-Language': getCurrentLanguage(),
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`文件上传失败: ${response.statusText || errorText}`);
      }

      const result = await response.json();

      if (result.code !== 0) {
        throw new Error(result.message || '文件上传失败');
      }

      if (result.code === 0 && result.data) {
        if (!result.data.list || result.data.list.length === 0) {
          console.error('上传成功但list为空:', result);
          return {
            code: 500,
            data: {} as AssetUploadResponse,
            message: '文件上传失败',
            success: false,
          };
        }

        const assetInfo = result.data.list[0];
        // url 对音频等非媒体预览类型可以为空，仅 asset_id 是必须的
        if (!assetInfo.asset_id) {
          console.error('上传成功但 asset_id 缺失:', assetInfo);
          return {
            code: 500,
            data: {} as AssetUploadResponse,
            message: '服务器返回的资产信息不完整',
            success: false,
          };
        }

        return {
          code: 0,
          data: {
            asset_id: assetInfo.asset_id,
            asset_type: assetInfo.asset_type || 0,
            size: assetInfo.size || 0,
            url: assetInfo.url,
            duration: assetInfo.duration,
          },
          message: 'ok',
          success: true,
        };
      }

      return result;
    } catch (error) {
      console.error('上传文件出错:', error);
      throw error;
    }
  }
  /**
   * 更新资产名称（重命名）
   * @param assetIdList 资产 ID 列表
   * @param name 新名称
   */
  async updateAssetName(assetIdList: string[], name: string): Promise<ApiResponse<null>> {
    return enhancedRequest.post<null>('/asset/update', {
      asset_id_list: assetIdList,
      name,
    }, {
      enableDeduplication: true,
      requestKey: `asset-update:${assetIdList.join(',')}`,
    });
  }
}

export default new AssetService();
